import type { RaceConfig } from '../../types/config.ts';
import type { MarketResult } from '../../types/markets.ts';
import type { ProviderState } from '../../types/ui.ts';
import { candidateById } from '../../lib/candidates.ts';
import { candidateColor } from '../../lib/colors.ts';
import { fmtProbability, ratio } from '../../lib/format.ts';
import { CandidateToken } from '../common/CandidateToken.tsx';
import { Provenance } from '../common/Provenance.tsx';
import { SourceState } from '../common/SourceState.tsx';

type MarketStates = Record<string, ProviderState<MarketResult>>;

/** Prediction-market odds, quarantined and labeled as sentiment. Never merged
 *  into the count; a race with no market shows a "not configured" panel rather
 *  than invented odds. */
export function MarketSentiment({
  race,
  marketStates,
  resolved,
}: {
  race: RaceConfig;
  marketStates: MarketStates;
  resolved: 'dark' | 'light';
}) {
  const providers = race.marketProviders.filter((p) => p.enabled);
  return (
    <div className="markets">
      <p className="markets__disclaimer">
        These are prediction-market odds — sentiment, not vote counts. Shown separately for that reason.
      </p>
      {providers.map((p) => {
        const st = marketStates[p.id];
        const pending = !st || st.status === 'loading' || st.status === 'idle';
        if (!st || st.status !== 'success' || !st.data) {
          return (
            <SourceState
              key={p.id}
              label={p.label}
              kind={pending ? null : st?.errorKind ?? null}
              loading={pending}
              message={st?.errorKind === 'not_configured' ? `No ${p.label} market configured for this race.` : null}
            />
          );
        }
        const data = st.data;
        const maxProb = Math.max(0.01, ...data.outcomes.map((o) => o.probability ?? 0));
        return (
          <div key={p.id} className="market">
            <p className="market__src">
              {p.label}
              {data.eventLabel ? <span className="dim"> · {data.eventLabel}</span> : null}
              {data.closed ? <span className="badge badge--muted">closed</span> : null}
            </p>
            <div className="market__rows">
              {data.outcomes.map((o, i) => {
                const cand = candidateById(race, o.candidateId);
                const color = cand ? candidateColor(cand, resolved) : 'var(--text-mid)';
                return (
                  <div key={`${o.label}-${i}`} className="market-row">
                    <span className="market-row__label">
                      {cand ? <CandidateToken candidate={cand} color={color} /> : o.label}
                    </span>
                    <span className="market-row__prob tabular">{fmtProbability(o.probability, 0)}</span>
                    <div className="market-row__bar" aria-hidden="true">
                      <div
                        className="market-row__fill"
                        style={{ width: `${ratio(o.probability, maxProb) * 100}%`, ['--cand-color' as string]: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <Provenance source={data.source} fetchedAt={data.fetchedAt} />
          </div>
        );
      })}
    </div>
  );
}
